import { useMemo } from 'react';

import { useLoaderData } from '@remix-run/react';
import {
  formatDate,
  hasNodeOfType,
  processTimestampsInTiptap,
  type TAudioPlayerElement,
  updateMergeTagText,
} from '@shared/dream-components';

import type { Page } from '~/interfaces/page';
import type { PodcastEpisode } from '~/interfaces/podcast';
import { getEpisodeHostedVideo } from '~/utils/podcasts/getEpisodeHostedVideo';
import { getEpisodeYoutubeVideoId } from '~/utils/podcasts/getEpisodeYoutubeVideoId';

import { serializeNode } from '../serializeNode';
import type { NodeWithMergeTag } from './types';

const ALLOWED_MERGE_TAGS = [
  'podcast.episode.image',
  'podcast.episode.publish_date',
  'podcast.episode.title',
  'podcast.episode.description',
  'podcast.episode.number',
  'podcast.episode.duration',
  'podcast.episode.details',
  'podcast.episode.audio_player',
  'podcast.episode.tiptap_content',
  'podcast.episode.content',
  'podcast.show.name',
] as const;

type AllowedMergeTag = (typeof ALLOWED_MERGE_TAGS)[number];

interface UsePodcastMergeTagProcessorProps {
  node: NodeWithMergeTag;
  children: React.ReactNode;
}

interface ProcessedResult {
  node: NodeWithMergeTag;
  children: React.ReactNode;
}

type LoaderDataWithPodcastEpisode = {
  podcastEpisode?: PodcastEpisode | null;
  page?: Page | null;
};

const usePodcastMergeTagProcessor = ({ node, children }: UsePodcastMergeTagProcessorProps): ProcessedResult => {
  const loaderData = useLoaderData() as LoaderDataWithPodcastEpisode;
  const podcastEpisode = loaderData?.podcastEpisode;
  const pageContent = loaderData?.page?.viewable_page_version?.content;

  return useMemo(() => {
    const mergeTag = node.attrs?.mergeTag;

    // Early return if no merge tag or not an allowed merge tag
    if (!mergeTag || !ALLOWED_MERGE_TAGS.includes(mergeTag as AllowedMergeTag)) {
      return { node, children };
    }

    // Early return if no product present
    if (!podcastEpisode) {
      return { node, children };
    }

    let elementToRender: NodeWithMergeTag = node;

    switch (mergeTag) {
      case 'podcast.show.name':
        elementToRender = updateMergeTagText(node, podcastEpisode.show.title || '') as NodeWithMergeTag;
        break;
      case 'podcast.episode.image': {
        const primaryImageUrl = podcastEpisode.thumbnail_url;
        if (primaryImageUrl) {
          elementToRender = {
            ...node,
            attrs: {
              ...node.attrs,
              alt: podcastEpisode.title,
              src: primaryImageUrl,
            },
          } as NodeWithMergeTag;
        }
        break;
      }
      case 'podcast.episode.publish_date':
        elementToRender = updateMergeTagText(node, formatDate(podcastEpisode.publish_date)) as NodeWithMergeTag;
        break;
      case 'podcast.episode.title':
        elementToRender = updateMergeTagText(node, podcastEpisode.title || '') as NodeWithMergeTag;
        break;
      case 'podcast.episode.description': {
        const description = podcastEpisode.description || '';
        elementToRender = updateMergeTagText(node, description) as NodeWithMergeTag;
        break;
      }
      case 'podcast.episode.number':
        elementToRender = updateMergeTagText(
          node,
          podcastEpisode.season_and_episode_number_display || '',
        ) as NodeWithMergeTag;
        break;
      case 'podcast.episode.duration':
        elementToRender = updateMergeTagText(node, podcastEpisode.duration.toString()) as NodeWithMergeTag;
        break;
      case 'podcast.episode.content': {
        // Backwards compatibility to support RSS feeds that are rendered on path /podcast/$episodeSlug/$feedId
        // Encoded HTML is extracted from RSS feed and since we can't always have all the supported tags
        // it makes sense to render it as HTML
        const renderAsHTML = podcastEpisode.tiptap_content === null && podcastEpisode.description_html;
        // Process timestamps in show notes to make them clickable
        const processedContent = podcastEpisode.tiptap_content
          ? processTimestampsInTiptap(podcastEpisode.tiptap_content)
          : podcastEpisode.tiptap_content;

        elementToRender = {
          ...node,
          attrs: {
            ...node.attrs,
            renderAsHTML,
            html: podcastEpisode?.description_html ?? null,
            tiptapState: processedContent,
          },
        } as NodeWithMergeTag;
        break;
      }
      case 'podcast.episode.audio_player': {
        const fullAudioUrl = podcastEpisode.audio_url || '';
        const previewAudioUrl = podcastEpisode.audio_preview_url || '';
        const audioUrl = fullAudioUrl || previewAudioUrl;
        const isPlayingPreview = podcastEpisode.is_premium && !fullAudioUrl && Boolean(previewAudioUrl);

        // The player only embeds the video on pages without a video block, which owns its placement.
        // It prefers the hosted video and falls back to YouTube.
        const embedsVideo = !hasNodeOfType(pageContent, 'podcastVideo');
        const hostedVideo = embedsVideo ? getEpisodeHostedVideo(podcastEpisode) : undefined;
        const youtubeVideoId = embedsVideo ? getEpisodeYoutubeVideoId(podcastEpisode) : undefined;

        const audioNode = node as TAudioPlayerElement;
        elementToRender = {
          ...audioNode,
          attrs: {
            ...audioNode.attrs,
            data: {
              ...audioNode.attrs?.data,
              title: podcastEpisode.title,
              audioType: podcastEpisode.audio_type ?? (audioUrl ? 'audio/mpeg' : undefined),
              audioUrl,
              thumbnailUrl: podcastEpisode.thumbnail_url,
              waveform: podcastEpisode.waveform,
              duration: isPlayingPreview ? undefined : podcastEpisode.duration,
              youtubeVideoId,
              hostedVideo,
              isPreview: isPlayingPreview,
            },
          },
        } as NodeWithMergeTag;
        break;
      }
      default:
        elementToRender = node;
    }

    const childrenToRender =
      'content' in elementToRender && elementToRender.content ? serializeNode(elementToRender.content) : children;

    return {
      node: elementToRender,
      children: childrenToRender,
    };
  }, [node, children, podcastEpisode, pageContent]);
};

export default usePodcastMergeTagProcessor;
